import { useTranslation } from 'react-i18next';

import { useActorResolver } from '~/hooks/use-actor-resolver';
import { useAuthStore } from '~/store/auth.store';

import type { RunRead } from '../types/run.api.types';

/**
 * Returns a matcher that is true when a handle / id / email names the signed-in user — so audit
 * strings the API sends (`lastActionBy`, archive `by`) can read "You" instead of a handle.
 */
export function useIsCurrentUser() {
  const user = useAuthStore((s) => s.user);
  const emailLocal = user?.email.split('@')[0] ?? null;
  return (value: string | null | undefined) =>
    !!value &&
    !!user &&
    (value === user.id ||
      value === user.email ||
      value === emailLocal ||
      value === user.displayName);
}

/**
 * Who started a run and who last touched it, as the runs grid's muted line says it: "by you",
 * "by ajeeth", "by schedule", "from experiment" — never an em-dash for "nobody acted".
 * `lastTouched` is set only when someone OTHER than the starter acted (G2 dedupe); the API
 * sends who and when, never which action.
 */
export function useRunsActor() {
  const { t } = useTranslation('catalog-enrichment-runs');
  const resolveActor = useActorResolver();
  const isMe = useIsCurrentUser();

  const nameOf = (value: string) => (isMe(value) ? t('table.actor.you') : value);

  return (run: RunRead): { startedBy: string | null; lastTouched: string | null } => {
    const creator = run.audit.createdBy ?? null;
    // No handle: resolve the id against the member list (same stopgap as ActorLabel).
    const resolved =
      creator && !creator.handle && creator.actorId
        ? resolveActor({
            id: creator.actorId,
            displayName: '',
            kind: creator.kind,
            email: null,
            avatarUrl: null,
            resolved: false,
          })
        : null;
    const handle = creator?.handle ?? (resolved?.resolved ? resolved.displayName : null);
    let startedBy: string | null = null;
    if (isMe(creator?.actorId)) startedBy = t('table.actor.byYou');
    else if (handle) startedBy = t('table.actor.by', { name: nameOf(handle) });
    else if (run.triggerSource === 'SCHEDULE') startedBy = t('table.actor.bySchedule');
    else if (run.triggerSource === 'EXPERIMENT') startedBy = t('table.actor.fromExperiment');

    const last = run.lastActionBy ?? null;
    const sameAsStarter =
      !!last &&
      (last === handle ||
        last === creator?.actorId ||
        (isMe(last) && (isMe(handle) || isMe(creator?.actorId))));
    // G2: "by you · last touched by you" collapses to "by you" — only a different actor is news.
    const lastTouched =
      last && !sameAsStarter ? t('table.actor.lastTouched', { name: nameOf(last) }) : null;
    return { startedBy, lastTouched };
  };
}
